import { CodexNativeStateUnavailable } from "./codex/native-state.js";
import { spawn, type ChildProcessWithoutNullStreams } from "node:child_process";
import type { NativeRequests, NativeServerRequests } from "./codex/native-protocol.generated.js";

/** The retained native request types, exposed only through this client surface. */
export type CodexRequestMethod = keyof NativeRequests;
/** Native callback handlers receive cancellation when their process is disposed. */
export type CodexServerRequestHandlers = {
  [M in keyof NativeServerRequests]?: (
    params: NativeServerRequests[M]["params"],
    signal: AbortSignal,
  ) => Promise<NativeServerRequests[M]["result"]>;
};
/** Explicit process inputs keep managed callers from inheriting ambient credentials. */
export interface CodexAppServerOptions {
  command: string;
  args: string[];
  cwd: string;
  env: Record<string, string>;
  clientInfo: NativeRequests["initialize"]["params"]["clientInfo"];
  requestTimeoutMs?: number;
  serverRequestTimeoutMs?: number;
  maxPendingRequests?: number;
  maxFrameBytes?: number;
  /** Isolate and reap the native process group on POSIX. */
  processGroup?: boolean;
  onServerRequest?: CodexServerRequestHandlers;
  /** Final synchronous lifecycle veto before a callback reply; cannot grant a missing handler. */
  canReplyToServerRequest?: (method: string, params: unknown) => boolean;
  onNotification?: (method: string, params: unknown) => void;
  /** Exactly once on terminal failure or disposal, even with no RPC pending. */
  onClose?: (error: CodexAppServerError) => void;
}
/** Safe error codes never contain stderr, native errors, RPC payloads or credentials. */
export class CodexAppServerError extends Error {
  constructor(
    readonly code: "closed" | "process" | "protocol" | "timeout" | "rejected" | "capacity",
  ) {
    super(`Codex App Server ${code}`);
    this.name = "CodexAppServerError";
  }
}
type Pending = {
  resolve: (value: unknown) => void;
  reject: (error: CodexAppServerError | CodexNativeStateUnavailable) => void;
  timer: ReturnType<typeof setTimeout>;
  resumeThreadId?: string;
};
const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const validId = (value: unknown): value is string | number =>
  typeof value === "string" || (typeof value === "number" && Number.isSafeInteger(value));
function bound(value: number | undefined, fallback: number, maximum: number): number {
  const result = value ?? fallback;
  if (!Number.isSafeInteger(result) || result <= 0 || result > maximum)
    throw new CodexAppServerError("protocol");
  return result;
}

/** One initialized stdio process with correlated, bounded requests and terminal disposal. */
export class CodexAppServerClient {
  private readonly pending = new Map<number, Pending>();
  private readonly callbacks = new Map<string | number, AbortController>();
  private readonly timeout: number;
  private readonly callbackTimeout: number;
  private readonly capacity: number;
  private readonly frameLimit: number;
  private readonly child: ChildProcessWithoutNullStreams;
  private readonly closed: Promise<void>;
  private terminal = false;
  private childClosed = false;
  private nextId = 1;
  private buffer = "";

  private constructor(private readonly options: CodexAppServerOptions) {
    this.timeout = bound(options.requestTimeoutMs, 15_000, 300_000);
    this.callbackTimeout = bound(options.serverRequestTimeoutMs, 30_000, 300_000);
    this.capacity = bound(options.maxPendingRequests, 64, 1024);
    this.frameLimit = bound(options.maxFrameBytes, 1024 * 1024, 16 * 1024 * 1024);
    this.child = spawn(options.command, options.args, {
      cwd: options.cwd,
      env: options.env,
      stdio: "pipe",
      detached: options.processGroup === true && process.platform !== "win32",
    });
    this.closed = new Promise((resolve) =>
      this.child.once("close", () => {
        this.childClosed = true;
        resolve();
      }),
    );
    this.child.stdout.setEncoding("utf8");
    this.child.stdout.on("data", (chunk: string) => this.receive(chunk));
    // Drain without retaining raw diagnostic output: native stderr can contain secrets.
    this.child.stderr.resume();
    this.child.on("error", () => this.fail("process"));
    this.child.stdin.on("error", () => this.fail("process"));
    this.child.stdout.on("error", () => this.fail("process"));
    this.child.stderr.on("error", () => this.fail("process"));
    this.child.once("exit", () => this.fail("process"));
  }

  /** Spawn and finish the native initialize/initialized handshake before returning. */
  static async connect(
    options: CodexAppServerOptions,
    signal?: AbortSignal,
  ): Promise<CodexAppServerClient> {
    let client: CodexAppServerClient | undefined;
    const abort = () => client?.fail("closed");
    try {
      if (signal?.aborted) throw new CodexAppServerError("closed");
      client = new CodexAppServerClient(options);
      signal?.addEventListener("abort", abort, { once: true });
      await client.request("initialize", {
        clientInfo: options.clientInfo,
        capabilities: { experimentalApi: true },
      });
      client.write({ method: "initialized" });
      return client;
    } catch (error) {
      await client?.dispose();
      throw error instanceof CodexAppServerError ? error : new CodexAppServerError("process");
    } finally {
      signal?.removeEventListener("abort", abort);
    }
  }

  /** Timeout closes this generation, so late replies can never satisfy a later request. */
  request<M extends CodexRequestMethod>(
    method: M,
    params: NativeRequests[M]["params"],
  ): Promise<NativeRequests[M]["result"]> {
    if (this.terminal) return Promise.reject(new CodexAppServerError("closed"));
    if (this.pending.size >= this.capacity)
      return Promise.reject(new CodexAppServerError("capacity"));
    const id = this.nextId++;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => this.fail("timeout"), this.timeout);
      this.pending.set(id, {
        resolve: resolve as (value: unknown) => void,
        reject,
        timer,
        ...(method === "thread/resume" &&
        params !== null &&
        "threadId" in params &&
        typeof params.threadId === "string"
          ? { resumeThreadId: params.threadId }
          : {}),
      });
      try {
        this.write({ id, method, params });
      } catch {
        this.fail("protocol");
      }
    });
  }

  /** Reject pending work immediately and wait a bounded interval for process shutdown. */
  async dispose(): Promise<void> {
    this.fail("closed");
    let timer: ReturnType<typeof setTimeout> | undefined;
    await Promise.race([
      this.closed,
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, 1000);
      }),
    ]);
    clearTimeout(timer);
  }

  /** Positive exit proof for callers that may replace a failed native generation. */
  get terminationConfirmed(): boolean {
    if (!this.childClosed) return false;
    if (!this.options.processGroup || process.platform === "win32" || !this.child.pid) return true;
    try {
      process.kill(-this.child.pid, 0);
      return false;
    } catch (error) {
      return (error as NodeJS.ErrnoException).code === "ESRCH";
    }
  }

  private fail(code: CodexAppServerError["code"]): void {
    if (this.terminal) return;
    this.terminal = true;
    this.buffer = "";
    for (const request of this.pending.values()) {
      clearTimeout(request.timer);
      request.reject(new CodexAppServerError(code));
    }
    this.pending.clear();
    for (const controller of this.callbacks.values()) controller.abort();
    this.callbacks.clear();
    if (this.options.processGroup && process.platform !== "win32" && this.child.pid) {
      try {
        process.kill(-this.child.pid, "SIGKILL");
      } catch {
        this.child.kill("SIGKILL");
      }
    } else this.child.kill("SIGKILL");
    try {
      this.options.onClose?.(new CodexAppServerError(code));
    } catch {
      // Observer failures cannot interrupt transport cleanup.
    }
  }

  private write(frame: unknown): void {
    if (this.terminal) throw new CodexAppServerError("closed");
    const line = `${JSON.stringify(frame)}\n`;
    if (Buffer.byteLength(line) > this.frameLimit) throw new CodexAppServerError("protocol");
    this.child.stdin.write(line);
  }

  private receive(chunk: string): void {
    if (this.terminal) return;
    this.buffer += chunk;
    try {
      for (;;) {
        const end = this.buffer.indexOf("\n");
        if (end < 0) break;
        const line = this.buffer.slice(0, end);
        this.buffer = this.buffer.slice(end + 1);
        if (Buffer.byteLength(line) > this.frameLimit) throw new CodexAppServerError("protocol");
        if (!line.trim()) continue;
        const frame: unknown = JSON.parse(line);
        if (!record(frame)) throw new CodexAppServerError("protocol");
        if (typeof frame.method === "string") {
          if ("id" in frame) {
            if (!validId(frame.id)) throw new CodexAppServerError("protocol");
            void this.handleServerRequest(frame.id, frame.method, frame.params);
          } else this.options.onNotification?.(frame.method, frame.params);
        } else {
          if (
            typeof frame.id !== "number" ||
            !Number.isSafeInteger(frame.id) ||
            "error" in frame === "result" in frame
          )
            throw new CodexAppServerError("protocol");
          const request = this.pending.get(frame.id);
          if (!request) throw new CodexAppServerError("protocol");
          clearTimeout(request.timer);
          this.pending.delete(frame.id);
          if ("error" in frame) {
            // Pinned 0.153.4 thread_store_resume_read_error maps only missing rollout
            // to this exact invalid-request message. Never retain raw native errors.
            const missing =
              request.resumeThreadId &&
              record(frame.error) &&
              frame.error.code === -32600 &&
              frame.error.message === `no rollout found for thread id ${request.resumeThreadId}`;
            request.reject(
              missing ? new CodexNativeStateUnavailable() : new CodexAppServerError("rejected"),
            );
          } else request.resolve(frame.result);
        }
        if (this.terminal) return;
      }
      if (Buffer.byteLength(this.buffer) > this.frameLimit)
        throw new CodexAppServerError("protocol");
    } catch {
      this.fail("protocol");
    }
  }

  private async handleServerRequest(
    id: string | number,
    method: string,
    params: unknown,
  ): Promise<void> {
    if (this.callbacks.has(id) || this.callbacks.size >= this.capacity)
      return this.fail("protocol");
    const controller = new AbortController();
    this.callbacks.set(id, controller);
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const handler = Object.hasOwn(this.options.onServerRequest ?? {}, method)
        ? this.options.onServerRequest?.[method as keyof NativeServerRequests]
        : undefined;
      if (!handler) throw new CodexAppServerError("rejected");
      const result = await Promise.race([
        Promise.resolve().then(() => {
          if (this.terminal || controller.signal.aborted) throw new CodexAppServerError("closed");
          return (handler as (params: unknown, signal: AbortSignal) => Promise<unknown>)(
            params,
            controller.signal,
          );
        }),
        new Promise<never>((_, reject) => {
          controller.signal.addEventListener(
            "abort",
            () => reject(new CodexAppServerError("closed")),
            { once: true },
          );
          timer = setTimeout(() => {
            controller.abort();
            reject(new CodexAppServerError("timeout"));
          }, this.callbackTimeout);
        }),
      ]);
      const current = this.options.canReplyToServerRequest?.(method, params) ?? true;
      if (
        !current ||
        this.terminal ||
        controller.signal.aborted ||
        this.callbacks.get(id) !== controller
      )
        throw new CodexAppServerError("closed");
      this.write({ id, result });
    } catch {
      if (!this.terminal) {
        try {
          this.write({ id, error: { code: -32603, message: "Codex client request failed" } });
        } catch {
          this.fail("protocol");
        }
      }
    } finally {
      clearTimeout(timer);
      controller.abort();
      this.callbacks.delete(id);
    }
  }
}
